/* eslint-disable jam3/no-sanitizer-with-danger, default-case, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */

import ExpansionPanel from '@material-ui/core/ExpansionPanel'
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails'
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary'
import { withStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import cn from 'classnames'
// biome-ignore lint/correctness/noUnusedImports: legacy dependency
import DOMPurify from 'dompurify'

import { DeleteButton, PrimaryButton, SecondaryButton } from '../Button'
import Icon from '../Icon'
import ToggleGroup from '../ToggleGroup'
import ToggleVisibility from '../ToggleVisibility'
import Tooltip from '../Tooltip'

const styles = theme => ({
  panelWrapper: {
    display: 'flex',
    position: 'relative',
  },
  dragHandle: {
    position: 'absolute',
    top: '50%',
    left: 13,
    fontSize: 10,
    transform: 'translateY(-50%)',
    color: `var(--gameday-questions-dragHandleColor, ${theme.questions.dragHandleColor})`,
  },
  lockedQuestion: {
    position: 'absolute',
    top: '50%',
    left: 13,
    transform: 'translateY(-50%)',
    color: `var(--gameday-buttons-iconColor, ${theme.buttons.iconColor})`,
    opacity: 0.68,
    '& svg': {
      display: 'block',
    },
  },
  panel: {
    width: '100%',
    backgroundColor: `var(--gameday-questions-questionBg, ${theme.questions.questionBg})`,
    border: `${theme.borders.default}`,
    boxShadow: 'none',
    borderRadius: '4px !important',
    padding: '0',
    position: 'relative',
    '&$disabled': {
      backgroundColor: `var(--gameday-questions-questionDisabledBg, ${theme.questions.questionDisabledBg})`,
    },
    '&:after': {
      content: '',
      width: '4px',
      position: 'absolute',
      opacity: '1 !important',
      top: '-1px',
      left: '-1px',
      bottom: '-1px',
      backgroundColor: theme.palette.primary.main,
      borderRadius: '2px 0 0 2px',
    },
    '&.isErrorOrDelete': {
      borderColor: theme.status.errorColor,
      '&:after': {
        backgroundColor: theme.status.errorColor,
      },
    },
  },
  expandIcon: {
    margin: '0 12px 0 10px',
    color: `var(--gameday-buttons-actionButtonColor, ${theme.buttons.actionButtonColor})`,
    '.Mui-disabled &': {
      padding: 0,
      margin: '0 12px 0 12px',
    },
  },
  expanded: {
    margin: '0 !important',
    '& .forExpansion': {
      overflow: 'visible',
    },
  },
  disabled: {
    '& .expand-icon': {
      display: 'none',
    },
  },
  docUpload: {
    border: `1px dashed ${`var(--gameday-inputField-inputBorder, ${theme.inputField.inputBorder})`}`,
    justifyContent: 'flex-start',
    width: '400px',
    '& span': {
      marginLeft: '10px',
      '& span': {
        color: theme.colors.lightBlue,
        marginLeft: '0px',
      },
    },
  },
  summary: {
    height: 68,
    display: 'flex',
    alignItems: 'center',
    padding: '0 0 0 58px',
    '&.isErrorOrDelete': {
      backgroundColor: theme.status.errorBG,
    },
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    padding: '0 0',
  },
  fieldLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 14,
    color: `var(--gameday-questions-fieldLabelColor, ${theme.questions.fieldLabelColor})`,
    '& p': {
      margin: 0,
    },
  },
  fieldIcon: {
    color: `var(--gameday-questions-fieldLabelColor, ${theme.questions.fieldLabelColor})`,
    marginRight: 22,
    transform: 'translateY(-2px)',
    '&:empty': {
      display: 'none',
    },
    '& svg': {
      width: 20,
      height: 20,
    },
  },
  coreFieldIcon: {
    width: 20,
    display: 'flex',
    alignItems: 'center',
    color: theme.mainNav.iconColor,
    opacity: 0.3,
    marginRight: 15,
    paddingBottom: 2,
    pointerEvents: 'all',
    userSelect: 'none',
  },
  summaryRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexBasis: '300px',
    padding: '0',
    marginLeft: 'auto',
    fontSize: theme.typography.fontSizeSm,
    color: `var(--gameday-body-textColorAlt, ${theme.body.textColorAlt})`,
    '& .labelExtra': {
      paddingLeft: '15px',
    },
  },
  questionConfig: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginRight: -4,
    '& .content-wrapper': {
      paddingBottom: '0 !important',
    },
    '& .toggle-group + button': {
      marginLeft: 25,
    },
    '& button': {
      pointerEvents: 'all',
    },
  },
  qNo: {
    textAlign: 'right',
  },
  summaryDisabled: {
    opacity: '1 !important',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    borderTop: `1px solid ${`var(--gameday-inputField-inputBorder, ${theme.inputField.inputBorder})`}`,
    padding: 0,
    '&.isErrorOrDelete': {
      borderColor: theme.status.errorColor,
    },
  },
  detailPadding: {
    padding: '28px 30px 0',
  },
  content: {
    display: 'block',
    width: '100%',
    '& > div:last-child': {
      marginTop: '36px',
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '13px',
    marginTop: '27px',
    borderTop: `1px solid ${`var(--gameday-inputField-inputBorder, ${theme.inputField.inputBorder})`}`,
    '& button': {
      marginLeft: '12px',
    },
  },
  avatar: {
    marginRight: '16px',
  },
  imageLabel: {
    opacity: '0.4',
  },
})

const IconDisplay = icon => {
  switch (icon) {
    case 'Text':
    case 'Email address':
      return <Icon iconName="fieldText" />
    case 'Number':
      return <Icon iconName="fieldNumber" />
    case 'Picklist':
    case 'MultiMemberType':
    case 'multiPicklist':
    case 'Qualification Standard':
      return <Icon iconName="fieldPickList" />
    case 'Date':
      return <Icon iconName="fieldDate" />
    case 'Checkbox':
    case 'Radio button':
      return <Icon iconName="fieldCheckbox" />
    case 'Image Upload':
    case 'Document Upload':
      return <Icon iconName="fieldUpload" />
    case 'TermsAndConditions':
      return <Icon iconName="fieldTermsConditions" />
    case 'User':
      return <Icon iconName="fieldUser" />
    case 'Header Text':
      return <Icon iconName="fieldHeaderText" />
    case 'Paragraph Text':
      return <Icon iconName="fieldParagraphText" />
    case 'Profile Image':
      return <Icon iconName="fieldProfileImage" />
    case 'Assigned Gender at Birth':
    case 'Gender at Birth':
    case 'Gender':
      return <Icon iconName="fieldGender" />
    case 'External':
      return <Icon iconName="externalLink2" />
  }

  return null
}

const QuestionAccordionItem = ({
  advancedOptions,
  classes,
  children,
  disabled,
  disableCancel,
  disableDelete,
  disableSave,
  draggable,
  expanded,
  onChange,
  handleCancel,
  handleDelete,
  handleSave,
  hideToolbar,
  isErrorOrDelete,
  labelExtra,
  name,
  questionNumber,
  questionType,
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  termsText,
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  text,
  toolbarConfig,
  showMandatoryToggle,
  isMandatory,
  onToggleMandatory,
  showVisibleToggle,
  isVisible,
  onToggleVisible,
  isConfigLocked,
  definedByGameDay,
  isCoreField,
}) => {
  const lockedByStg = labelExtra === 'Locked by SportsTG'

  let iconType = questionType
  if (name === 'Profile Image' || ['Assigned Gender at Birth', 'Gender at Birth', 'Gender'].includes(name)) {
    iconType = name
  }
  if (definedByGameDay && (name === 'Team Code for Meet Manager' || name === 'Team Name for Meet Manager')) {
    iconType = 'External'
  }

  return (
    <div className={classes.panelWrapper}>
      <ExpansionPanel
        classes={{
          root: classes.panel,
          disabled: classes.disabled,
          expanded: classes.expanded,
        }}
        disabled={disabled}
        className={cn({ isErrorOrDelete })}
        CollapseProps={{
          classes: {
            container: cn(classes.container, 'forExpansion'),
          },
        }}
        expanded={expanded}
        onChange={onChange}
      >
        <ExpansionPanelSummary
          classes={{
            root: cn({ isErrorOrDelete }, classes.summary),
            content: cn({ isErrorOrDelete }, classes.summaryContent),
            disabled: classes.summaryDisabled,
            expandIcon: classes.expandIcon,
          }}
          expandIcon={<ExpandMoreIcon className="expand-icon" />}
          IconButtonProps={{
            disableRipple: true,
          }}
        >
          {draggable && (
            <div className={classes.dragHandle}>
              <Icon iconName="fieldDragHandle" />
            </div>
          )}
          {lockedByStg && (
            <div className={classes.lockedQuestion}>
              <Icon iconSize={16} iconName="lock" />
            </div>
          )}
          <Tooltip content="Core Member Field" placement="right">
            {/** biome-ignore lint/a11y/noStaticElementInteractions: TODO */}
            {/** biome-ignore lint/a11y/useKeyWithClickEvents: TODO */}
            <div className={classes.coreFieldIcon} onClick={e => e.stopPropagation()}>
              {isCoreField && <Icon iconName="coreField" iconSize={20} />}
            </div>
          </Tooltip>
          <div className={classes.fieldIcon}>{IconDisplay(iconType)}</div>
          {questionType === 'Header Text' || questionType === 'Paragraph Text' ? (
            // biome-ignore lint/security/noDangerouslySetInnerHtml: TODO
            <div className={classes.fieldLabel} dangerouslySetInnerHTML={{ __html: name }} />
          ) : (
            <div className={classes.fieldLabel}>{name}</div>
          )}
          <div className={classes.summaryRight}>
            {(showMandatoryToggle || showVisibleToggle) && (
              <div className={classes.questionConfig}>
                {showMandatoryToggle && (
                  <ToggleGroup
                    toggles={[
                      { value: 'true', label: 'Mandatory' },
                      { value: 'false', label: 'Optional' },
                    ]}
                    default={String(isMandatory)}
                    onChange={onToggleMandatory}
                    disabled={isConfigLocked}
                  />
                )}
                {showVisibleToggle && (
                  <ToggleVisibility isVisible={isVisible} onToggle={onToggleVisible} disabled={isConfigLocked} />
                )}
              </div>
            )}
            {labelExtra && !lockedByStg && <div className={classes.extraInfo}>{labelExtra}</div>}
            <div className={classes.qNo}>
              {!disabled && questionNumber && `${questionType === 'TermsAndConditions' ? 'T' : 'Q'}${questionNumber}`}
            </div>
          </div>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails
          classes={{
            root: cn({ isErrorOrDelete }, classes.details),
          }}
        >
          <div className={classes.detailPadding}>
            <div className={classes.content}>{children}</div>
            {advancedOptions && advancedOptions}
          </div>
          {!hideToolbar && (
            <div className={classes.toolbar}>
              {/* biome-ignore lint/complexity/useOptionalChain: TODO */}
              {toolbarConfig && toolbarConfig.includes('delete') && (
                <DeleteButton onClick={handleDelete} disabled={disableDelete} aria-label="Remove Question">
                  Remove Question
                </DeleteButton>
              )}
              {/* biome-ignore lint/complexity/useOptionalChain: TODO */}
              {toolbarConfig && toolbarConfig.includes('cancel') && (
                <SecondaryButton onClick={handleCancel} disabled={disableCancel} aria-label="Cancel">
                  Cancel
                </SecondaryButton>
              )}
              {/* biome-ignore lint/complexity/useOptionalChain: TODO */}
              {toolbarConfig && toolbarConfig.includes('save') && (
                <PrimaryButton onClick={handleSave} disabled={disableSave} aria-label="Save">
                  Save
                </PrimaryButton>
              )}
            </div>
          )}
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </div>
  )
}

QuestionAccordionItem.defaultProps = {
  disableCancel: false,
  disableDelete: false,
  disableSave: false,
  draggable: false,
  handleCancel: () => {},
  handleDelete: () => {},
  handleSave: () => {},
  hideToolbar: false,
  isErrorOrDelete: false,
  labelExtra: '',
  questionNumber: null,
  questionType: '',
  termsText: '',
  text: '',
  toolbarConfig: [],
  showMandatoryToggle: false,
  showVisibleToggle: false,
  onToggleMandatory: () => {},
  onToggleVisible: () => {},
}

const QuestionAccord = withStyles(styles)(QuestionAccordionItem)
QuestionAccord.displayName = 'QuestionAccordionItem'
export default QuestionAccord
